// import { useFlagsStatus } from '@unleash/proxy-client-react'
import { FC, ReactNode, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { RouteConfig } from '@app/routing/RenderRoutes'

import { Navigate } from '@lib/react-router-dom'

import { AnalyticsPage } from '@pages/Trade/Analytics/Analytics.page'
import { MarketsPage } from '@pages/Trade/Markets/Markets.page'
import { FixTradingWebSocketProvider } from '@pages/Trade/Portfolio/context/FixTradingWebSocketContext'
import { TradeLayout } from '@pages/Trade/TradeLayout'
import { TransactionsPage } from '@pages/Traders-Room/Transactions/Transactions.page'

import { useAccountInfo } from '@context/AccountInfoContext/AccountInfoContext.Provider'
import { useSessionEntity } from '@context/EntityContext'
import { useProductReadContext } from '@context/ProductContext'

import { useAccountAccessStatuses } from '@utils/AccountAccess/accountAccessStatuses'
import { TickmillCompaniesEnum } from '@utils/companyName.utils'
import { useBasePath } from '@utils/path'

import { Loading } from '@shared/components/Loading/Loading'

import { PortfolioPage } from './Portfolio/Portfolio.page'

const TradeRouteGuard: FC<{ children: ReactNode }> = ({ children }) => {
  const { isDefaultCFDProductType } = useProductReadContext()
  const entity = useSessionEntity()
  const { canAccessTrade, isLoading: isAccountInfoLoading } = useTradeRoutesConditions()
  // const { flagsReady } = useFlagsStatus()
  const { redirectionLink } = useBasePath()

  const isTMSC = isDefaultCFDProductType() && entity === TickmillCompaniesEnum.TICKMILL_SC

  if (isAccountInfoLoading) {
    return <Loading isLoading showLoadingIcon />
  }

  if (!isTMSC || !canAccessTrade) {
    return <Navigate to={redirectionLink} replace />
  }

  return <>{children}</>
}

export const useTradeRoutes = (): RouteConfig[] => {
  const { t } = useTranslation()

  return useMemo(
    (): RouteConfig[] => [
      {
        path: 'trade',
        element: <Navigate to={'markets'} />,
        layout: 'full-no-container',
        outlet: (
          <TradeRouteGuard>
            <TradeLayout />
          </TradeRouteGuard>
        ),
        meta: { breadcrumbLabel: t('Tabs.Trade') },
        children: [
          {
            path: 'markets',
            element: (
              <FixTradingWebSocketProvider>
                <MarketsPage />
              </FixTradingWebSocketProvider>
            ),
            meta: { breadcrumbLabel: t('Tabs.Markets') },
          },
          {
            path: 'portfolio',
            element: <Navigate to={'./open'} />,
            outlet: <PortfolioPage />,
            meta: { breadcrumbLabel: t('Tabs.Portfolio') },
            children: [
              { path: 'open', element: <></> },
              { path: 'pending', element: <></> },
              { path: 'history', element: <></> },
              { path: '*', element: <Navigate to='.' /> },
            ],
          },
          {
            path: 'analytics',
            element: <AnalyticsPage />,
            meta: { breadcrumbLabel: t('Tabs.Analytics') },
          },
        ],
      },
      {
        path: 'trade/transaction',
        element: <TransactionsPage />,
        layout: 'full',
        meta: { breadcrumbLabel: t('Tabs.Transactions') },
      },
    ],
    [t]
  )
}
export const useTradeRoutesConditions = () => {
  const { accountInfoLimits, isLoading } = useAccountInfo()
  const accountAccessStatuses = useAccountAccessStatuses()

  return {
    canAccessTrade:
      accountInfoLimits?.isTickmillTraderTradingAllowed &&
      accountAccessStatuses.isAccountActivatedStatus,
    isLoading,
  }
}
